import React, { useState, useEffect } from 'react';
import { GlassButton } from '../ui/GlassButton';
import { Send } from 'lucide-react';
import { apiClient as api } from '../../services/api';

/** Normalize FastAPI error detail — may be a string or a Pydantic validation error array */
const getApiError = (e: any, fallback: string): string => {
  const detail = e?.response?.data?.detail;
  if (!detail) return fallback;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    return detail.map((d: any) => d?.msg ?? JSON.stringify(d)).join('; ');
  }
  return fallback;
};


interface Customer {
  buyer_id: string;
  email: string;
  name?: string;
  total_spent: number;
  order_count: number;
  last_purchase_at: string;
}

const ProductCustomers: React.FC = () => {
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [resendingId, setResendingId] = useState<string | null>(null);
  const [resendMsg, setResendMsg] = useState<{ id: string; msg: string; ok: boolean } | null>(null);

  useEffect(() => {
    loadCustomers();
  }, []);

  const loadCustomers = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await api.get('/products/customers');
      setCustomers(res.data);
    } catch (err: any) {
      setError('Failed to load customers.');
    } finally {
      setLoading(false);
    }
  };

  const handleResendDownloads = async (customer: Customer) => {
    setResendingId(customer.buyer_id);
    setResendMsg(null);
    try {
      await api.post(`/products/customers/${customer.buyer_id}/resend-downloads`);
      setResendMsg({ id: customer.buyer_id, msg: 'Download links resent.', ok: true });
    } catch (err: any) {
      setResendMsg({
        id: customer.buyer_id,
        msg: getApiError(err, 'Failed to resend. Please try again.'),
        ok: false,
      });
    } finally {
      setResendingId(null);
    }
  };

  const formatDate = (iso: string) =>
    new Date(iso).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });

  if (loading) return <div className="products-loading">Loading customers...</div>;

  return (
    <div className="customers-container">
      <div className="customers-header">
        <h2>Customers</h2>
        <span style={{ fontSize: '14px', color: 'var(--text-secondary)' }}>
          {customers.length} total
        </span>
      </div>

      {error && <div className="products-error">{error}</div>}

      {customers.length === 0 ? (
        <div className="products-empty-state">
          <p>No customers yet. They will appear here once orders are placed.</p>
        </div>
      ) : (
        <div className="customers-table-wrapper">
          <table className="customers-table">
            <thead>
              <tr>
                <th>Email</th>
                <th>Name</th>
                <th>Orders</th>
                <th>Total Spent</th>
                <th>Last Purchase</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {customers.map(customer => (
                <tr key={customer.buyer_id}>
                  <td style={{ fontWeight: 500 }}>{customer.email}</td>
                  <td>{customer.name || <span style={{ color: 'var(--text-muted)' }}>—</span>}</td>
                  <td>{customer.order_count}</td>
                  <td style={{ fontWeight: 600 }}>${customer.total_spent.toFixed(2)}</td>
                  <td style={{ whiteSpace: 'nowrap' }}>
                    {customer.last_purchase_at ? formatDate(customer.last_purchase_at) : '—'}
                  </td>
                  <td>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      <GlassButton
                        variant="secondary"
                        size="sm"
                        icon={<Send size={13} />}
                        onClick={() => handleResendDownloads(customer)}
                        loading={resendingId === customer.buyer_id}
                        title="Resend download links"
                      >
                        Resend Downloads
                      </GlassButton>
                      {resendMsg && resendMsg.id === customer.buyer_id && (
                        <span
                          style={{
                            fontSize: '12px',
                            color: resendMsg.ok ? 'var(--success)' : 'var(--danger)',
                          }}
                        >
                          {resendMsg.msg}
                        </span>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default ProductCustomers;
